import { queryOptions, useMutation, useQuery } from '@tanstack/react-query';
import type { BulkCreateDocumentItem } from '@/api/documents';
import {
  bulkArchiveDocuments,
  bulkCreateDocuments,
  cancelDocumentAccessRequest,
  downloadDocuments,
  fetchDocumentPermissions,
  fetchDocuments,
  fetchDocumentTypes,
  fetchNetworkDocuments,
  makeDocumentPublic,
  patchDocument,
  requestDocumentAccess,
  shareDocument,
} from '@/api/documents';

// ─── Params type (encoding belongs in the logic layer) ───────────────────────

export interface DocumentsParams {
  page: number;
  count: number;
  sort?: string;
  filter?: string;
}

// ─── Query string builders ────────────────────────────────────────────────────

function buildOwnDocumentsQs(params: DocumentsParams): string {
  const qs = new URLSearchParams();
  qs.set('page', String(params.page));
  qs.set('count', String(params.count));
  qs.set('isWithNetworkDocuments', 'true');
  if (params.sort) qs.set('sort', params.sort);
  if (params.filter) qs.set('filter', params.filter);
  return qs.toString();
}

function buildNetworkDocumentsQs(
  companyId: string,
  params: DocumentsParams,
): string {
  const qs = new URLSearchParams();
  qs.set('page', String(params.page));
  qs.set('count', String(params.count));
  qs.set('companyId', companyId);
  if (params.sort) qs.set('sort', params.sort);
  if (params.filter) qs.set('filter', params.filter);
  return qs.toString();
}

function buildRequestAccessQs(params: DocumentsParams): string {
  const qs = new URLSearchParams();
  qs.set('page', String(params.page));
  qs.set('count', String(params.count));
  qs.set('isNetworkShared', 'false');
  if (params.sort) qs.set('sort', params.sort);
  if (params.filter) qs.set('filter', params.filter);
  return qs.toString();
}

function buildDocumentTypesQs(): string {
  return new URLSearchParams({
    sort: 'title:ASC',
    filter: JSON.stringify({ isDeprecated: '0' }),
  }).toString();
}

// ─── Query key factories ──────────────────────────────────────────────────────

export const documentKeys = {
  ownList: (companyId: string, params: DocumentsParams) =>
    ['network', 'company', companyId, 'documents', params, 'own'] as const,

  networkList: (companyId: string, params: DocumentsParams) =>
    ['network', 'company', companyId, 'documents', params, 'network'] as const,

  requestAccess: (params: DocumentsParams) =>
    ['documents', 'request-access', params] as const,

  types: () => ['document-types'] as const,
};

// ─── queryOptions factories (usable in route loaders) ────────────────────────

export function ownDocumentsQueryOptions(
  companyId: string,
  params: DocumentsParams,
) {
  return queryOptions({
    queryKey: documentKeys.ownList(companyId, params),
    queryFn: () => fetchDocuments(buildOwnDocumentsQs(params)),
    staleTime: 60_000,
  });
}

export function networkDocumentsQueryOptions(
  companyId: string,
  params: DocumentsParams,
) {
  return queryOptions({
    queryKey: documentKeys.networkList(companyId, params),
    queryFn: () => fetchDocuments(buildNetworkDocumentsQs(companyId, params)),
    staleTime: 60_000,
  });
}

export function requestAccessQueryOptions(params: DocumentsParams) {
  return queryOptions({
    queryKey: documentKeys.requestAccess(params),
    queryFn: () => fetchNetworkDocuments(buildRequestAccessQs(params)),
    staleTime: 60_000,
  });
}

export function documentTypesQueryOptions() {
  return queryOptions({
    queryKey: documentKeys.types(),
    queryFn: () => fetchDocumentTypes(buildDocumentTypesQs()),
    staleTime: 5 * 60_000,
  });
}

// ─── Hooks ───────────────────────────────────────────────────────────────────

export function useDocumentsQuery(
  companyId: string,
  params: DocumentsParams,
  isOwnCompany: boolean,
) {
  const ownQuery = useQuery({
    ...ownDocumentsQueryOptions(companyId, params),
    enabled: isOwnCompany,
  });
  const networkQuery = useQuery({
    ...networkDocumentsQueryOptions(companyId, params),
    enabled: !isOwnCompany,
  });
  return isOwnCompany ? ownQuery : networkQuery;
}

export function useRequestAccessDocuments(params: DocumentsParams) {
  return useQuery(requestAccessQueryOptions(params));
}

export function useDocumentTypes() {
  return useQuery(documentTypesQueryOptions());
}

export function useDocumentPermissions(id: string | null) {
  return useQuery({
    queryKey: ['document-permissions', id],
    queryFn: () => fetchDocumentPermissions(id!),
    enabled: !!id,
    staleTime: 30_000,
  });
}

export function usePatchDocument() {
  return useMutation({
    mutationFn: ({
      id,
      body,
    }: {
      id: string;
      body: { name?: string; documentTypeId?: string };
    }) => patchDocument(id, body),
  });
}

export function useBulkArchiveDocuments() {
  return useMutation({
    mutationFn: (ids: string[]) => bulkArchiveDocuments(ids),
  });
}

export function useBulkCreateDocuments() {
  return useMutation({
    mutationFn: (payload: {
      companyId: string;
      documents: BulkCreateDocumentItem[];
    }) => bulkCreateDocuments(payload),
  });
}

export function useDownloadDocuments() {
  return useMutation({
    mutationFn: (ids: string[]) => downloadDocuments(ids),
  });
}

export function useShareDocument() {
  return useMutation({
    mutationFn: ({ id, companyIds }: { id: string; companyIds: string[] }) =>
      shareDocument(id, companyIds),
  });
}

export function useMakeDocumentPublic() {
  return useMutation({
    mutationFn: (id: string) => makeDocumentPublic(id),
  });
}

export function useRequestDocumentAccess() {
  return useMutation({
    mutationFn: (id: string) => requestDocumentAccess(id),
  });
}

export function useCancelDocumentAccessRequest() {
  return useMutation({
    mutationFn: (id: string) => cancelDocumentAccessRequest(id),
  });
}
